import { computed, type ComputedRef } from 'vue';
import { useUser } from '@/store/user';
import * as selectedBrandStorage from '@lib/selected-brand-storage';
import { intercomShutdown } from '@lib/intercom';

export interface PlmBrand {
  /** Company connection id — the brand itself is `brandID`. */
  id: number;
  brandID: number;
  name: string;
}

/**
 * The brands the user can switch between — PLM-enabled company connections only.
 * Non-PLM connections (suppliers, etc.) have no usable brandId in this UI and would
 * render as disabled junk entries.
 */
export function usePlmBrands(): ComputedRef<PlmBrand[]> {
  return computed(() => useUser().companies.filter((c: any) => c.plm && c.brandID) as PlmBrand[]);
}

/** The brands other than the one currently open. */
export function useOtherPlmBrands(): ComputedRef<PlmBrand[]> {
  const brands = usePlmBrands();
  return computed(() => brands.value.filter((brand) => brand.brandID !== useUser().brandId));
}

/**
 * Switch the tab to another brand and reload into `destination`.
 *
 * The default lands on a clean products page — the saved view/filters belong to the previous
 * brand, so it skips the '/' redirect that re-applies them via ?view=. A caller that knows where
 * the reader wanted to go (a deep link into a product that turned out to live in another brand)
 * passes that path instead. The active season is stored per brand, so it needs no clearing here.
 */
export function switchBrand(brandId: number, destination = '/plm/products'): void {
  selectedBrandStorage.write(brandId);
  // Reset Intercom before the reload re-boots it. The logged-in user is unchanged
  // across a brand switch, so without a shutdown the post-reload boot would restore
  // the previous brand's Messenger session (open conversation, state) from cookies.
  // Shutting down clears those cookies so the new boot starts a clean chat associated
  // with the new brand's company. Mirrors logOut().
  intercomShutdown();
  window.location.assign(destination);
}

export function switchBrandInNewTab(brandId: number) {
  // sessionStorage is copied to a new tab when opened by window.open() (per HTML
  // spec, unlike user-initiated middle/Ctrl-click on a link). Seed it with the
  // target brand so the new tab boots there, then restore the parent tab's value.
  selectedBrandStorage.withSessionSeed(brandId, () => window.open('/plm/products', '_blank'));
}

/** Click handling shared by every brand row: modified click opens the brand in a new tab. */
export function onBrandClick(event: MouseEvent, brandId: number) {
  if (!brandId || brandId === useUser().brandId) return;
  if (event.ctrlKey || event.metaKey || event.shiftKey) {
    switchBrandInNewTab(brandId);
    return;
  }
  switchBrand(brandId);
}

export function onBrandMousedown(event: MouseEvent, brandId: number) {
  if (event.button !== 1) return;
  if (!brandId || brandId === useUser().brandId) return;
  event.preventDefault();
  switchBrandInNewTab(brandId);
}
